import { useEffect, useRef, useState } from 'react';

/**
 * Authenticated `/api/ws/dashboard` WebSocket subscription.
 *
 * Design notes:
 *   - The latest `onMessage` is held in a ref so a parent re-render with a
 *     non-memoised callback does NOT churn the socket (root cause of the
 *     historical reconnection chatter).
 *   - Exponential back-off with jitter (capped at 30 s) prevents thundering
 *     herd reconnect storms.
 *   - Auth failures (4401/4403) terminate the loop — no point hammering an
 *     endpoint that's rejecting our token.
 *   - A single mount/unmount cycle = at most one open socket. The cleanup
 *     flag stops a pending reconnect from racing past unmount.
 */
const MAX_ATTEMPTS = 8;
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 30000;
// iter489 — Custom WS close codes
const AUTH_CLOSE_CODES = new Set([4401, 4403]);  // terminate: bad token
const WARMUP_CLOSE_CODE = 4503;                  // backend cold-start; retry slowly
const BASE_WARMUP_RETRY_MS = 10000;              // 10s back-off when backend says "warming up"

export function useWebSocket(onMessage) {
  const [isConnected, setIsConnected] = useState(false);
  const handlerRef = useRef(onMessage);
  handlerRef.current = onMessage;

  useEffect(() => {
    let ws = null;
    let pingTimer = null;
    let reconnectTimer = null;
    let attempt = 0;
    let stopped = false;

    const cleanupSocket = () => {
      if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
      if (ws) { try { ws.close(); } catch { /* ignore */ } ws = null; }
    };

    const connect = () => {
      if (stopped || attempt >= MAX_ATTEMPTS) return;
      attempt += 1;
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      // iter938 — bridge PIN-only cashier sessions: fall back to cashier_token
      // so POS gets the live ws:dashboard bus (stock-request popups). The WS
      // backend already accepts employee/cashier JWTs. Also fall back to
      // waiter_token so the Waiter screen receives live kitchen-status updates.
      const token = localStorage.getItem('token') || localStorage.getItem('cashier_token') || localStorage.getItem('waiter_token') || '';
      if (!token) {
        // No auth yet (e.g. the Waiter/POS PIN-login screen is still showing,
        // so the token isn't in localStorage until the operator logs in).
        // Wait for it to appear, then connect — WITHOUT burning the attempt
        // budget. Bounded by the component's mount lifetime.
        if (!stopped) reconnectTimer = setTimeout(connect, 3000);
        return;
      }
      const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
      const wsHost = backendUrl.replace(/^https?:\/\//, '');
      // Token travels in the Sec-WebSocket-Protocol header, never in the URL (URLs are logged by proxies).
      const url = `${wsProtocol}://${wsHost}/api/ws/dashboard`;

      try {
        ws = new WebSocket(url, ['smarto.bearer', token]);
      } catch {
        scheduleReconnect();
        return;
      }

      ws.onopen = () => {
        if (stopped) { try { ws.close(); } catch { /* ignore */ } return; }
        setIsConnected(true);
        attempt = 0;
        pingTimer = setInterval(() => {
          if (ws && ws.readyState === WebSocket.OPEN) {
            try { ws.send(JSON.stringify({ type: 'ping' })); } catch { /* ignore */ }
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          try { window.dispatchEvent(new CustomEvent('ws:dashboard', { detail: data })); } catch { /* ignore */ }
          if (data.type !== 'pong' && handlerRef.current) handlerRef.current(data);
        } catch { /* ignore malformed frame */ }
      };

      ws.onerror = () => { /* swallow — onclose drives reconnects */ };

      ws.onclose = (ev) => {
        setIsConnected(false);
        if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
        ws = null;
        if (stopped) return;
        if (ev && AUTH_CLOSE_CODES.has(ev.code)) return; // auth fail — don't retry
        // iter489 — Backend explicitly said it's still warming up. Wait 10s,
        // don't bump `attempt` so we don't burn the MAX_ATTEMPTS budget.
        if (ev && ev.code === WARMUP_CLOSE_CODE) {
          const jitter = Math.floor(Math.random() * 2000);
          reconnectTimer = setTimeout(connect, BASE_WARMUP_RETRY_MS + jitter);
          return;
        }
        scheduleReconnect();
      };
    };

    const scheduleReconnect = () => {
      if (stopped || attempt >= MAX_ATTEMPTS) return;
      const expo = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * Math.pow(2, attempt - 1));
      const jitter = Math.floor(Math.random() * 500);
      reconnectTimer = setTimeout(connect, expo + jitter);
    };

    connect();

    return () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      cleanupSocket();
    };
  }, []); // mount-once

  return { isConnected };
}
